import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import { fetchTemplates, fetchTemplateById, deleteTemplate, previewApplyTemplate } from "../../Utils/api";
import "./TemplateLibraryModal.css";

// Diff styling for the side-by-side "Now → After restore" view
const posBadge = {
  width: 18, height: 18, borderRadius: "50%", background: "#eef2f6", color: "#002244",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  fontSize: 10, fontWeight: 700, flex: "none",
};
const chipAdd = { height: 16, fontSize: 10, ml: "6px", bgcolor: "#cdebd6", color: "#2e7d32" };

const DAYS_OF_WEEK = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

// Group template stops by employee+day, sorted within each group by routeOrder.
const groupStops = (stops) => {
  const map = new Map();
  (stops || []).forEach((stop) => {
    const key = `${stop.employeeId ?? ""}|${stop.dayOfWeek ?? ""}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        employeeName: stop.employeeName || null,
        dayOfWeek: stop.dayOfWeek,
        stops: [],
      });
    }
    map.get(key).stops.push(stop);
  });
  map.forEach((g) => g.stops.sort((a, b) => (a.routeOrder ?? 999) - (b.routeOrder ?? 999)));
  return Array.from(map.values()).sort((a, b) => {
    const d = (a.dayOfWeek ?? 7) - (b.dayOfWeek ?? 7);
    return d !== 0 ? d : (a.employeeName || "").localeCompare(b.employeeName || "");
  });
};

const SCOPE_LABELS = {
  DAY: "Day",
  WEEK: "Employee · Week",
  COMPANY_DAY: "Company · Day",
  COMPANY_WEEK: "Company · Week",
};

const scopeOf = (t) => (t?.scope ? t.scope : "DAY");

// Unique key per conflict row — one customer can conflict with several employees on
// the same day, so the choice is keyed by (customer, source employee, day), not customer.
// Resolution is keyed per (customer, day) — NOT per source employee — because a customer
// can only have ONE permanent home per day. If the same customer is currently on two other
// employees' routes that day, they share a single choice, so you can't pick contradictory
// resolutions (e.g. Permanent on one source + This-week-only on another) that would leave
// the customer still double-booked. The chosen action is then applied to every source.
const reassignmentKey = (r) => `${r.customerId}|${r.dayOfWeek}`;

const summarizeTemplate = (t) => {
  const parts = [];
  const stops = t.stopCount != null ? t.stopCount : (t.stops?.length ?? 0);
  parts.push(`${stops} stop${stops !== 1 ? "s" : ""}`);
  if (t.employeeCount > 1) parts.push(`${t.employeeCount} employees`);
  if (t.dayCount > 1) parts.push(`${t.dayCount} days`);
  else if (t.dayOfWeek != null) parts.push(DAYS_OF_WEEK[t.dayOfWeek]);
  return parts.join(" · ");
};

const TemplateLibraryModal = ({
  open,
  onClose,
  onImport,
  onRestore,
  onApplyRoutes,
  companyId,
  employeeId,
  canManage,
}) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [conflictMode, setConflictMode] = useState("OVERWRITE");
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  // Restore preview (diff) state
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  // Editable staging: keyed by "empId|day" → { employeeId, dayOfWeek, employeeName,
  // dayName, included:[{customerId,poolId,name,isNew}], removedPool:[{...}] }
  const [editRoutes, setEditRoutes] = useState({});
  // Per-customer temporary/permanent choice for cross-employee conflicts.
  // Key = customerId, value = "TEMPORARY" | "PERMANENT". Defaults to PERMANENT.
  const [reassignmentChoices, setReassignmentChoices] = useState({});

  useEffect(() => {
    if (open) {
      loadTemplates();
    } else {
      setSelectedTemplate(null);
      setSearch("");
      setError("");
    }
  }, [open]);

  const loadTemplates = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchTemplates(companyId, employeeId);
      setTemplates(Array.isArray(data) ? data : []);
    } catch {
      setError("Failed to load templates.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = async (template) => {
    setLoadingDetail(true);
    setError("");
    try {
      const detail = await fetchTemplateById(template.templateId, companyId);
      setSelectedTemplate(detail);
    } catch {
      setError("Failed to load template details.");
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDelete = async (templateId) => {
    setDeleting(true);
    setError("");
    try {
      await deleteTemplate(templateId, companyId);
      setTemplates((prev) => prev.filter((t) => t.templateId !== templateId));
      if (selectedTemplate?.templateId === templateId) setSelectedTemplate(null);
    } catch {
      setError("Failed to delete template.");
    } finally {
      setDeleting(false);
    }
  };

  const handleImport = () => {
    if (!selectedTemplate) return;
    onImport(selectedTemplate);
    onClose();
  };

  // Step 1: fetch the dry-run diff, seed the editable staging, show the preview
  const handleReviewRestore = async () => {
    if (!selectedTemplate) return;
    setLoadingPreview(true);
    setError("");
    try {
      const preview = await previewApplyTemplate(selectedTemplate.templateId, {
        companyId,
        conflictMode,
      });
      // Seed editable routes from the proposed "after" state
      const er = {};
      (preview.routes || []).forEach((r) => {
        const key = `${r.employeeId}|${r.dayOfWeek}`;
        const currentSet = new Set((r.beforeOrder || []).map((c) => c.customerId));
        const included = (r.afterOrder || []).map((c) => ({
          customerId: c.customerId, poolId: c.poolId, name: c.name, isNew: !currentSet.has(c.customerId),
        }));
        const inclSet = new Set(included.map((c) => c.customerId));
        const removedPool = (r.beforeOrder || [])
          .filter((c) => !inclSet.has(c.customerId))
          .map((c) => ({ customerId: c.customerId, poolId: c.poolId, name: c.name, isNew: false }));
        er[key] = {
          employeeId: r.employeeId, dayOfWeek: r.dayOfWeek,
          employeeName: r.employeeName, dayName: r.dayName,
          included, removedPool,
        };
      });
      setEditRoutes(er);
      setPreviewData(preview);
      // Default every conflict row to PERMANENT so the user must actively opt into
      // "temporary"/"delete". Keyed per (customer, source employee, day) so two rows for
      // the same customer (on different employees) are independent.
      const initChoices = {};
      (preview.reassignments || []).forEach((r) => { initChoices[reassignmentKey(r)] = "PERMANENT"; });
      setReassignmentChoices(initChoices);
      setShowPreview(true);
    } catch {
      setError("Could not load the restore preview. Please try again.");
    } finally {
      setLoadingPreview(false);
    }
  };

  // ---- editable staging operations ----
  const updateRoute = (key, mutate) => {
    setEditRoutes((prev) => {
      const route = prev[key];
      if (!route) return prev;
      const next = { ...route, included: [...route.included], removedPool: [...route.removedPool] };
      mutate(next);
      return { ...prev, [key]: next };
    });
  };
  const moveStop = (key, idx, dir) => updateRoute(key, (r) => {
    const j = idx + dir;
    if (j < 0 || j >= r.included.length) return;
    [r.included[idx], r.included[j]] = [r.included[j], r.included[idx]];
  });
  const removeStop = (key, idx) => updateRoute(key, (r) => {
    const [c] = r.included.splice(idx, 1);
    if (c) r.removedPool.push(c);
  });
  const addBackStop = (key, customerId) => {
    setEditRoutes((prev) => {
      const route = prev[key];
      if (!route) return prev;
      // Enforce one-employee-per-day: block re-adding a customer who is already placed on a
      // DIFFERENT employee's route for the same day (e.g. they're being moved there by a
      // "Permanent" reassignment). Without this, clicking "add back" recreates the customer
      // on this employee while they're also being restored to another employee that day —
      // the exact cross-employee same-day duplicate we must prevent.
      const clashKey = Object.keys(prev).find((k) =>
        k !== key &&
        prev[k].dayOfWeek === route.dayOfWeek &&
        prev[k].included.some((c) => c.customerId === customerId)
      );
      if (clashKey) {
        const clashEmp = prev[clashKey].employeeName || "another employee";
        const dayLabel = route.dayName || "that day";
        setError(`That customer is already on ${clashEmp}'s ${dayLabel} route after this restore. A customer can only be on one employee's route per day — remove them there first if you want them here instead.`);
        return prev;
      }
      const i = route.removedPool.findIndex((c) => c.customerId === customerId);
      if (i < 0) return prev;
      const nextRemoved = [...route.removedPool];
      const [moved] = nextRemoved.splice(i, 1);
      return {
        ...prev,
        [key]: { ...route, included: [...route.included, moved], removedPool: nextRemoved },
      };
    });
  };

  // Resolve every conflict row into: the per-source action list the backend applies,
  // the set of (customer|day) to drop from the restored route (DELETE rows), and which
  // (customer|day) should be created as a one-week-only stop (every row TEMPORARY).
  const buildReassignment = () => {
    const actions = [];
    const deleted = new Set();
    const allTempByCustDay = new Map();
    (previewData?.reassignments || []).forEach((r) => {
      const action = reassignmentChoices[reassignmentKey(r)] || "PERMANENT";
      actions.push({
        customerId: r.customerId,
        fromEmployeeId: r.fromEmployeeId,
        dayOfWeek: r.dayOfWeek,
        action,
      });
      const cd = `${r.customerId}|${r.dayOfWeek}`;
      if (action === "DELETE") deleted.add(cd);
      // A customer-day is a temporary stop only if it has conflict rows and ALL are TEMPORARY.
      if (!allTempByCustDay.has(cd)) allTempByCustDay.set(cd, true);
      if (action !== "TEMPORARY") allTempByCustDay.set(cd, false);
    });
    return { actions, deleted, allTempByCustDay };
  };

  // Turn a staging map ({ "emp|day": { employeeId, dayOfWeek, included } }) into the
  // routes payload, dropping DELETE'd customers and flagging temporary stops.
  const buildRoutesPayload = (routeMap, reassign) =>
    Object.values(routeMap).map((r) => ({
      employeeId: r.employeeId,
      dayOfWeek: r.dayOfWeek,
      stops: r.included
        .filter((c) => !reassign.deleted.has(`${c.customerId}|${r.dayOfWeek}`))
        .map((c) => ({
          customerId: c.customerId,
          poolId: c.poolId,
          temporary: reassign.allTempByCustDay.get(`${c.customerId}|${r.dayOfWeek}`) === true,
        })),
    }));

  // Directly restore from template stops when the preview reports no changes.
  // One-click — no second "Confirm Restore" step needed.
  const handleForceApply = async () => {
    const stops = (selectedTemplate?.stops || [])
      .slice()
      .sort((a, b) => (a.routeOrder ?? 999) - (b.routeOrder ?? 999));
    const er = {};
    stops.forEach((stop) => {
      if (stop.employeeId == null || stop.dayOfWeek == null || !stop.poolId) return;
      const key = `${stop.employeeId}|${stop.dayOfWeek}`;
      if (!er[key]) {
        er[key] = {
          employeeId: stop.employeeId,
          dayOfWeek: stop.dayOfWeek,
          included: [],
          removedPool: [],
        };
      }
      er[key].included.push({
        customerId: stop.customerId,
        poolId: stop.poolId,
        name: `${stop.customerFirstName || ""} ${stop.customerLastName || ""}`.trim(),
        isNew: false,
      });
    });

    if (Object.keys(er).length === 0) {
      setError("No stops found in template — nothing to restore.");
      return;
    }

    setRestoring(true);
    setError("");
    try {
      const reassign = buildReassignment();
      const routes = buildRoutesPayload(er, reassign);
      if (onApplyRoutes) {
        await onApplyRoutes(routes, conflictMode, reassign.actions);
      } else if (onRestore) {
        await onRestore(selectedTemplate, conflictMode);
      }
      setShowPreview(false);
      setPreviewData(null);
      setEditRoutes({});
      onClose();
    } catch {
      setError("Failed to restore. Please try again.");
    } finally {
      setRestoring(false);
    }
  };

  // Step 2: user confirmed — apply the (possibly edited) routes explicitly
  const handleConfirmRestore = async () => {
    setRestoring(true);
    setError("");
    try {
      const reassign = buildReassignment();
      const routes = buildRoutesPayload(editRoutes, reassign);
      if (onApplyRoutes) {
        await onApplyRoutes(routes, conflictMode, reassign.actions);
      } else if (onRestore) {
        await onRestore(selectedTemplate, conflictMode); // fallback
      }
      setShowPreview(false);
      setPreviewData(null);
      setEditRoutes({});
      onClose();
    } catch {
      setError("Failed to restore. Please try again.");
    } finally {
      setRestoring(false);
    }
  };

  const closePreview = () => {
    setShowPreview(false);
    setPreviewData(null);
    setEditRoutes({});
    setReassignmentChoices({});
  };

  const filtered = templates.filter((t) =>
    t.templateName.toLowerCase().includes(search.toLowerCase())
  );

  const hasNoChanges =
    previewData &&
    previewData.totalAdded === 0 &&
    previewData.totalRemoved === 0 &&
    previewData.totalReordered === 0;

  return (
    <>
    <Modal open={open} onClose={onClose}>
      <Box className="template-library-modal">
        <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "12px" }}>
          Template Library
        </Typography>

        <Box className="template-library-body">
          {/* Left — template list */}
          <Box className="template-list-panel">
            <TextField
              placeholder="Search templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              fullWidth
              sx={{ marginBottom: "10px" }}
              inputProps={{ autoCorrect: "off", spellCheck: false }}
            />

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", padding: "24px" }}>
                <CircularProgress size={28} />
              </Box>
            ) : filtered.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ padding: "12px 0" }}>
                {templates.length === 0 ? "No templates saved yet." : "No templates match your search."}
              </Typography>
            ) : (
              <List dense disablePadding>
                {filtered.map((t) => (
                  <React.Fragment key={t.templateId}>
                    <ListItem
                      disablePadding
                      secondaryAction={
                        canManage && (
                          <Tooltip title="Delete template">
                            <IconButton
                              size="small"
                              color="error"
                              disabled={deleting}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(t.templateId);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )
                      }
                    >
                      <ListItemButton
                        selected={selectedTemplate?.templateId === t.templateId}
                        onClick={() => handleSelectTemplate(t)}
                        sx={{ paddingRight: canManage ? "40px" : "12px" }}
                      >
                        <ListItemText
                          primary={t.templateName}
                          secondary={summarizeTemplate(t)}
                        />
                        <Chip
                          label={SCOPE_LABELS[scopeOf(t)] || "Day"}
                          size="small"
                          sx={{ marginLeft: "6px", fontSize: "10px", backgroundColor: "#e3edf7" }}
                        />
                        {t.isAutoBackup && (
                          <Chip label="Auto" size="small" color="success" sx={{ marginLeft: "4px", fontSize: "10px" }} />
                        )}
                        {!t.isShared && (
                          <Chip label="Private" size="small" sx={{ marginLeft: "4px", fontSize: "10px" }} />
                        )}
                      </ListItemButton>
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>

          {/* Right — stop preview */}
          <Box className="template-detail-panel">
            {loadingDetail ? (
              <Box sx={{ display: "flex", justifyContent: "center", padding: "24px" }}>
                <CircularProgress size={28} />
              </Box>
            ) : selectedTemplate ? (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                    {selectedTemplate.templateName}
                  </Typography>
                  <Chip
                    label={SCOPE_LABELS[scopeOf(selectedTemplate)] || "Day"}
                    size="small"
                    sx={{ fontSize: "10px", backgroundColor: "#e3edf7" }}
                  />
                </Box>
                {selectedTemplate.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ marginBottom: "6px" }}>
                    {selectedTemplate.description}
                  </Typography>
                )}
                {(selectedTemplate.origEmpName || selectedTemplate.dayOfWeek != null) && (
                  <Typography variant="body2" color="text.secondary" sx={{ marginBottom: "8px", fontSize: "0.78rem" }}>
                    {selectedTemplate.origEmpName && <>Originally for: <strong>{selectedTemplate.origEmpName}</strong></>}
                    {selectedTemplate.origEmpName && selectedTemplate.dayOfWeek != null && "  ·  "}
                    {selectedTemplate.dayOfWeek != null && <>Day: <strong>{DAYS_OF_WEEK[selectedTemplate.dayOfWeek]}</strong></>}
                  </Typography>
                )}
                <Divider sx={{ marginBottom: "10px" }} />
                <Typography variant="body2" sx={{ fontWeight: 600, marginBottom: "6px" }}>
                  Stop Order ({selectedTemplate.stops?.length ?? 0} customers)
                </Typography>
                <Box className="template-stop-list">
                  {groupStops(selectedTemplate.stops).map((group) => (
                    <Box key={group.key}>
                      {(group.employeeName || group.dayOfWeek != null) && (
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, color: "#002244", display: "block",
                                mt: "10px", mb: "4px", textTransform: "uppercase", letterSpacing: ".5px" }}
                        >
                          {[group.employeeName, group.dayOfWeek != null ? DAYS_OF_WEEK[group.dayOfWeek] : null]
                            .filter(Boolean).join(" · ")}
                        </Typography>
                      )}
                      {group.stops.map((stop, idx) => (
                        <Box key={stop.stopId ?? idx} className="template-stop-row">
                          <Typography variant="body2" className="stop-order-badge">
                            {idx + 1}
                          </Typography>
                          <Typography variant="body2">
                            {stop.customerFirstName} {stop.customerLastName}
                            {stop.customerAddress && (
                              <span className="stop-address"> — {stop.customerAddress}</span>
                            )}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Select a template to preview its stops.
              </Typography>
            )}
          </Box>
        </Box>

        {error && (
          <Typography variant="body2" color="error" sx={{ marginTop: "8px" }}>
            {error}
          </Typography>
        )}

        {/* Restore (backup-recovery) controls */}
        {selectedTemplate && onRestore && (
          <Box sx={{ marginTop: "14px", padding: "10px 12px", backgroundColor: "#f6f9fc", borderRadius: "6px" }}>
            <FormControl>
              <FormLabel sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#002244" }}>
                Restore mode
              </FormLabel>
              <RadioGroup
                row
                value={conflictMode}
                onChange={(e) => setConflictMode(e.target.value)}
              >
                <FormControlLabel
                  value="OVERWRITE"
                  control={<Radio size="small" />}
                  label={<span style={{ fontSize: "0.82rem" }}>Overwrite — replace the route(s) entirely</span>}
                />
                <FormControlLabel
                  value="MERGE"
                  control={<Radio size="small" />}
                  label={<span style={{ fontSize: "0.82rem" }}>Merge — add only missing customers</span>}
                />
              </RadioGroup>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Restore rebuilds work orders for every employee &amp; day this template covers. Use this to recover a corrupted route.
            </Typography>
            {conflictMode === "OVERWRITE" && (
              <Box sx={{ marginTop: "8px", padding: "8px 12px", backgroundColor: "#fdecea",
                          border: "1px solid #f5a3a3", borderRadius: "6px" }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#b71c1c", display: "block" }}>
                  ⚠ Overwrite will replace the entire active schedule for every employee &amp; day this template covers.
                </Typography>
                <Typography variant="caption" sx={{ color: "#b71c1c", display: "block", marginTop: "2px" }}>
                  Anything currently scheduled that isn't in this template will be cancelled. Consider saving the
                  current schedule as a template first so you can get back to it.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "16px" }}>
          <Button variant="outlined" onClick={onClose} disabled={restoring}>
            Cancel
          </Button>
          {scopeOf(selectedTemplate) === "DAY" && (
            <Button
              variant="outlined"
              onClick={handleImport}
              disabled={!selectedTemplate || restoring}
            >
              Add to current schedule
            </Button>
          )}
          {onRestore && (
            <Button
              variant="contained"
              onClick={handleReviewRestore}
              disabled={!selectedTemplate || loadingPreview}
              startIcon={loadingPreview ? <CircularProgress size={16} color="inherit" /> : <RestoreIcon />}
              sx={{ backgroundColor: "#002244" }}
            >
              {loadingPreview ? "Loading…" : "Review & Restore"}
            </Button>
          )}
        </Box>
      </Box>
    </Modal>

    {/* ---- Restore preview (diff) confirmation ---- */}
    <Modal open={showPreview} onClose={closePreview}>
      <Box className="template-library-modal" sx={{ maxHeight: "85vh", overflowY: "auto" }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "4px" }}>
          Review changes before restoring
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: "12px" }}>
          {selectedTemplate?.templateName} ·{" "}
          {conflictMode === "OVERWRITE"
            ? "Overwrite — routes will be replaced entirely"
            : "Merge — only missing customers will be added"}
        </Typography>

        {conflictMode === "OVERWRITE" && (
          <Box sx={{ padding: "8px 12px", backgroundColor: "#fdecea", border: "1px solid #f5a3a3",
                      borderRadius: "6px", marginBottom: "12px" }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#b71c1c", display: "block" }}>
              ⚠ This replaces the entire active schedule for the affected employees &amp; days.
            </Typography>
            <Typography variant="caption" sx={{ color: "#b71c1c", display: "block", marginTop: "2px" }}>
              Customers currently scheduled but not in this template will be cancelled. If you haven't already,
              save the current schedule as a template first so you can return to it.
            </Typography>
          </Box>
        )}

        {previewData && (
          <>
            <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
              <Chip size="small" color="success" label={`+${previewData.totalAdded} added`} />
              {conflictMode === "OVERWRITE" && (
                <Chip size="small" color="error" label={`−${previewData.totalRemoved} removed`} />
              )}
              {conflictMode === "OVERWRITE" && (
                <Chip size="small" color="info" label={`${previewData.totalReordered} reordered`} />
              )}
              <Chip size="small" label={`${previewData.totalUnchanged} unchanged`} />
              <Chip size="small" variant="outlined" label={`${previewData.routesAffected} route(s) affected`} />
            </Box>

            {previewData.reassignments?.length > 0 && (
              <Box sx={{ padding: "10px 14px", backgroundColor: "#fff3e0", border: "1px solid #ffcc80",
                          borderRadius: "6px", marginBottom: "12px" }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#e65100", marginBottom: "2px" }}>
                  ⚠ These customers are currently on another employee's route. Choose how to handle each:
                </Typography>
                <Typography variant="caption" sx={{ color: "#bf360c", display: "block", marginBottom: "10px" }}>
                  Each customer below is on a different employee's route than this template. Pick what should happen — each button says exactly who ends up with the customer.
                </Typography>
                {(() => {
                  // Collapse conflicts to one row per (customer, day), listing every source
                  // employee the customer is currently on. One coherent choice per customer.
                  const groups = [];
                  const byKey = {};
                  previewData.reassignments.forEach((r) => {
                    const key = reassignmentKey(r);
                    if (!byKey[key]) {
                      byKey[key] = { key, sample: r, fromNames: [] };
                      groups.push(byKey[key]);
                    }
                    if (r.fromEmployeeName && !byKey[key].fromNames.includes(r.fromEmployeeName)) {
                      byKey[key].fromNames.push(r.fromEmployeeName);
                    }
                  });
                  return groups.map(({ key, sample, fromNames }) => {
                    const choice = reassignmentChoices[key] || "PERMANENT";
                    const sources = fromNames.length > 0 ? fromNames.join(" & ") : sample.fromEmployeeName;
                    const target = sample.toEmployeeName || "this template's employee";
                    const choiceBtn = (value, label, color, hover) => (
                      <Button
                        size="small"
                        variant={choice === value ? "contained" : "outlined"}
                        onClick={() => setReassignmentChoices((prev) => ({ ...prev, [key]: value }))}
                        sx={{
                          fontSize: "0.72rem", py: "5px", px: "10px", textTransform: "none",
                          justifyContent: "flex-start", textAlign: "left", lineHeight: 1.3,
                          ...(choice === value
                            ? { backgroundColor: color, borderColor: color, color: "#fff",
                                "&:hover": { backgroundColor: hover } }
                            : { borderColor: color, color: color }),
                        }}
                      >
                        {label}
                      </Button>
                    );
                    return (
                      <Box key={key} sx={{ mb: "10px", pb: "8px", borderBottom: "1px dashed #ffcc80" }}>
                        <Typography variant="body2" sx={{ color: "#bf360c", mb: "6px" }}>
                          <strong>{sample.customerName}</strong> is currently on <strong>{sources}</strong>'s {sample.dayName} route
                          {sample.toEmployeeName
                            ? <>, but this template puts them on <strong>{sample.toEmployeeName}</strong>'s {sample.dayName}</>
                            : null}. What should happen?
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "5px", maxWidth: 540 }}>
                          {choiceBtn("PERMANENT", `Move ${sample.customerName} to ${target} permanently (remove from ${sources})`, "#002244", "#001a33")}
                          {choiceBtn("TEMPORARY", `${target} covers this week only — ${sample.customerName} stays on ${sources}'s route`, "#f57c00", "#e65100")}
                          {choiceBtn("DELETE", `Remove ${sample.customerName} from ${sample.dayName} entirely (neither employee)`, "#c62828", "#b71c1c")}
                        </Box>
                      </Box>
                    );
                  });
                })()}
              </Box>
            )}

            {hasNoChanges ? (
              <Box sx={{ padding: "16px", backgroundColor: "#fff8e1", borderRadius: "6px", marginBottom: "12px" }}>
                <Typography variant="body2" sx={{ mb: "10px" }}>
                  Your work orders already match this template structure. However, if the schedule page shows fewer customers than expected, work order dates may have drifted — re-applying will recreate them with fresh dates.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleForceApply}
                  disabled={restoring}
                  startIcon={restoring ? <CircularProgress size={14} color="inherit" /> : null}
                  sx={{ borderColor: "#002244", color: "#002244" }}
                >
                  {restoring ? "Restoring…" : "Re-apply Anyway"}
                </Button>
              </Box>
            ) : (
              <Box sx={{ marginBottom: "12px" }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: "8px" }}>
                  Review the result on the right. You can reorder (↑ ↓), drop (✕), or add a customer back before confirming.
                </Typography>

                {previewData.routes?.map((route) => {
                  const key = `${route.employeeId}|${route.dayOfWeek}`;
                  const edit = editRoutes[key];
                  const empId = route.employeeId;
                  const day = route.dayOfWeek;
                  // Reflect the chosen conflict resolution live in these panels (DISPLAY ONLY —
                  // the payload/delta logic is unchanged). Map each conflict customer on this day
                  // to its from/to employees so we can tell, for THIS route, whether the customer
                  // is being moved IN (target) or moved OUT of (source).
                  const conflictInfoByCust = {};
                  (previewData.reassignments || []).forEach((r) => {
                    if (r.dayOfWeek === day) {
                      conflictInfoByCust[r.customerId] = {
                        fromEmployeeId: r.fromEmployeeId, toEmployeeId: r.toEmployeeId,
                        toEmployeeName: r.toEmployeeName, fromEmployeeName: r.fromEmployeeName,
                      };
                    }
                  });
                  const choiceForCust = (custId) => reassignmentChoices[`${custId}|${day}`] || "PERMANENT";
                  const roleForCust = (custId) => {
                    const info = conflictInfoByCust[custId];
                    if (!info) return null;
                    if (info.toEmployeeId === empId) return "target";
                    if (info.fromEmployeeId === empId) return "source";
                    return null;
                  };
                  // Source-side customers kept this week (choice = "This week only"): shown as a
                  // "stays" row instead of a removed chip, since the source employee keeps them.
                  const keptThisWeek = (edit?.removedPool || []).filter(
                    (c) => roleForCust(c.customerId) === "source" && choiceForCust(c.customerId) === "TEMPORARY"
                  );
                  const keptIds = new Set(keptThisWeek.map((c) => c.customerId));
                  return (
                  <Box
                    key={key}
                    sx={{ border: "1px solid #e0e6ed", borderRadius: "6px", padding: "10px 12px", marginBottom: "10px" }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold", marginBottom: "8px" }}>
                      {route.employeeName} · {route.dayName}
                    </Typography>

                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 20px 1.25fr", gap: "8px", alignItems: "start" }}>
                      {/* NOW (read-only) */}
                      <Box sx={{ background: "#fafcff", border: "1px solid #e0e6ed", borderRadius: "6px", padding: "6px 8px" }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#7a869a", letterSpacing: ".4px", mb: "4px" }}>
                          NOW
                        </Typography>
                        {(route.beforeOrder?.length ? route.beforeOrder : [{ name: "— empty —", change: "blank" }]).map((c, i) => (
                          <Box key={`b${i}`} sx={{ display: "flex", alignItems: "center", gap: "6px", py: "2px" }}>
                            {c.change !== "blank" && <span style={posBadge}>{i + 1}</span>}
                            <Typography variant="body2">{c.name}</Typography>
                          </Box>
                        ))}
                      </Box>

                      <Box sx={{ alignSelf: "center", textAlign: "center", color: "#7a869a", fontSize: 18 }}>→</Box>

                      {/* AFTER (editable) */}
                      <Box sx={{ background: "#fafcff", border: "1px solid #e0e6ed", borderRadius: "6px", padding: "6px 8px" }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#7a869a", letterSpacing: ".4px", mb: "4px" }}>
                          AFTER RESTORE — editable
                        </Typography>
                        {edit && (edit.included.length > 0 || keptThisWeek.length > 0) ? (
                          <>
                          {edit.included.map((c, i) => {
                            const role = roleForCust(c.customerId);
                            const choice = choiceForCust(c.customerId);
                            const willDelete = role === "target" && choice === "DELETE";
                            const isThisWeek = role === "target" && choice === "TEMPORARY";
                            return (
                              <Box key={`af${c.customerId}`} sx={{ display: "flex", alignItems: "center", gap: "4px", py: "2px",
                                     borderRadius: "4px", px: "4px",
                                     background: (!willDelete && c.isNew) ? "#e9f7ee" : "transparent",
                                     opacity: willDelete ? 0.5 : 1 }}>
                                <span style={posBadge}>{i + 1}</span>
                                <Typography variant="body2" sx={{ flex: 1, textDecoration: willDelete ? "line-through" : "none" }}>
                                  {c.name}
                                  {isThisWeek && <Chip size="small" label="this week only" sx={{ ml: "6px", height: 18, fontSize: "10px", bgcolor: "#f57c00", color: "#fff" }} />}
                                  {willDelete && <Chip size="small" label="won't be added" sx={{ ml: "6px", height: 18, fontSize: "10px", bgcolor: "#c62828", color: "#fff" }} />}
                                  {role !== "target" && c.isNew && <Chip size="small" label="new" sx={chipAdd} />}
                                </Typography>
                                <IconButton size="small" disabled={i === 0} onClick={() => moveStop(key, i, -1)} sx={{ p: "2px" }}>
                                  <KeyboardArrowUpIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" disabled={i === edit.included.length - 1} onClick={() => moveStop(key, i, 1)} sx={{ p: "2px" }}>
                                  <KeyboardArrowDownIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => removeStop(key, i)} sx={{ p: "2px" }}>
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            );
                          })}
                          {keptThisWeek.map((c) => (
                            <Box key={`kept${c.customerId}`} sx={{ display: "flex", alignItems: "center", gap: "4px", py: "2px", px: "4px" }}>
                              <span style={posBadge}>•</span>
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {c.name}
                                <Chip size="small" label="stays" sx={{ ml: "6px", height: 18, fontSize: "10px", bgcolor: "#2e7d32", color: "#fff" }} />
                                <span style={{ fontSize: 11, color: "#7a869a" }}>
                                  {" "}— {conflictInfoByCust[c.customerId]?.toEmployeeName || "the other tech"} covers this week
                                </span>
                              </Typography>
                            </Box>
                          ))}
                          </>
                        ) : (
                          <Typography variant="caption" color="error">Route will be cleared (no customers).</Typography>
                        )}

                        {/* add-back pool */}
                        {edit && edit.removedPool.filter((c) => !keptIds.has(c.customerId)).length > 0 && (
                          <Box sx={{ mt: "6px", pt: "6px", borderTop: "1px dashed #d6dee8" }}>
                            <Typography sx={{ fontSize: 10, color: "#7a869a", mb: "3px" }}>Removed — click to add back:</Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                              {edit.removedPool.filter((c) => !keptIds.has(c.customerId)).map((c) => {
                                // Gray out + disable when this customer is already placed on a
                                // DIFFERENT employee's route for the same day — adding them back
                                // here would create a cross-employee same-day duplicate.
                                const clashElsewhere = Object.keys(editRoutes).some((k) =>
                                  k !== key &&
                                  editRoutes[k].dayOfWeek === edit.dayOfWeek &&
                                  editRoutes[k].included.some((x) => x.customerId === c.customerId)
                                );
                                return (
                                  <Chip key={`rm${c.customerId}`} size="small"
                                    label={clashElsewhere ? `+ ${c.name} (on another route)` : `+ ${c.name}`}
                                    onClick={clashElsewhere ? undefined : () => addBackStop(key, c.customerId)}
                                    disabled={clashElsewhere}
                                    sx={{ fontSize: 11, cursor: clashElsewhere ? "not-allowed" : "pointer",
                                          bgcolor: "#eef2f6", opacity: clashElsewhere ? 0.45 : 1 }} />
                                );
                              })}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                  );
                })}
              </Box>
            )}

            {previewData.skipped?.length > 0 && (
              <Box sx={{ padding: "8px 12px", backgroundColor: "#fff8e1", borderRadius: "6px", marginBottom: "12px" }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: "block", marginBottom: "2px" }}>
                  Will be skipped:
                </Typography>
                {previewData.skipped.map((s, i) => (
                  <Typography key={i} variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    • {s}
                  </Typography>
                ))}
              </Box>
            )}
          </>
        )}

        {error && (
          <Typography variant="body2" color="error" sx={{ marginBottom: "8px" }}>
            {error}
          </Typography>
        )}

        <Box sx={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <Button variant="outlined" onClick={closePreview} disabled={restoring}>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmRestore}
            disabled={restoring || Object.keys(editRoutes).length === 0}
            startIcon={restoring ? <CircularProgress size={16} color="inherit" /> : <RestoreIcon />}
            sx={{ backgroundColor: "#002244" }}
          >
            {restoring ? "Restoring…" : "Confirm Restore"}
          </Button>
        </Box>
      </Box>
    </Modal>
    </>
  );
};

export default TemplateLibraryModal;
